/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useMutation } from "@apollo/client";
import { Helmet } from "react-helmet";
import { v4 as uuidv4 } from "uuid";

import * as reduxStore from "../../redux/store";
import styles from "../Locations/Locations.module.css";
import brStyles from "./StoreBrandRefresh.module.css";
import {
  formatBrandRefreshStoreDistance,
  formatBrandRefreshStorePhone,
  formatMilesAsStoreDistance,
  isMissingBrandRefreshStoreDistance,
} from "./storeBrandRefreshClasses";
import { haversineDistance, metersToMiles } from "../../shared/utilities/distance";

import * as userActions from "../../redux/actions/user";
import * as loyaltyAction from '../../redux/actions/loyalty';
import * as countryActions from "../../redux/actions/country";
import * as addressActions from "../../redux/actions/address";
import * as elementsActions from "../../redux/actions/elements";

import * as storeRepo from "../../shared/repos/graphql/store";
import * as orderRepo from "../../shared/repos/graphql/order";

import { orderMethods, orderTypeIds } from "../../shared/constants/order";
import * as phoneService from "../../shared/utilities/phone";
import { UPDATE_ORDER_LOCATION } from "../../graphql/order";
import { shippingMethods, storeTypes } from "../../shared/constants/store";

import Loader from "../../components/Loader/Loader";
import Button from "../../components/Button/Button";
import Link from "../../components/LinkWithCountry/LinkWithCountry";
import MapComponent from "../../components/MapComponent/MapComponent";
import DeliveryPickUpWidget from "../../components/DeliveryPickUpWidget/DeliveryPickUpWidget";
import endpoints from "../../shared/constants/endpoints";
import { routeCountryPath } from "../../shared/utilities/common";
import { handlePickupSubmit } from "../Locations/renderings";

import { setSessionToken } from "../../shared/utilities/config";
import { isBrandRefreshEnabled } from "../../shared/utilities/brandRefresh";
import {
  getStorefrontImageSrc,
  handleStorefrontImageError,
} from "../../shared/utilities/storefrontFallback";
import { getStorePickupHoursStatus } from "../../shared/utilities/storeHoursStatus";
import { isE2EStoreHoursBypassEnabled } from "../../shared/utilities/e2eFlags";
import {setCaptchaToken} from "../../shared/utilities/captcha";
import captchaAction from "../../shared/constants/captchaAction";
import ReCAPTCHAForm from "../../components/ReCaptcha/ReCaptcha";
import elementConstants from "../../shared/constants/element";
import httpStatusConstant from "../../shared/constants/httpStatus";


const exitIcon = require("../Locations/imgs/close-icon.svg");
const lightExitIcon = require("../Locations/imgs/light-exit-icon.svg");
const mobileBackIcon = require("../Locations/imgs/mobile-back.svg");

class Store extends Component {
  constructor(props) {
    super(props);

    this.state = {
      store: null,
      stores: [],
      showDefaultMap: false,
      showModal: false,
      addressType: "pickup",
      hasCaptchaError: false,
      showCaptchaChallenge: false,
      skipGenerateNewToken: false,
    };
  }

  componentDidMount = async () => {
    setSessionToken(uuidv4());
    // eslint-disable-next-line react/prop-types
    const {useRecaptcha, setVerificationToken, setVerificationAction } = this.props;

    if (useRecaptcha === 1) {
      const { skipGenerateNewToken } = this.state;
      setCaptchaToken(setVerificationToken, setVerificationAction, captchaAction.addressSearching, skipGenerateNewToken);
    }

    await this.getStores();   
  };

  getStores = async () => {
    try {
      const { match, addressComponents } = this.props;
      const { params } = match;
      const { slug } = params;
      const storeData = await storeRepo.getStoreBySlug(slug);
      let store = storeData?.data?.storeBySlug;
      let stores = [];
      if (store) {
        // Brand-refresh Store Details shows a single pin — skip nearby search for map.
        if (!isBrandRefreshEnabled()) {
          const result = await storeRepo.getStores({
            lat: store.lat,
            lng: store.lng
          });
          stores = result?.data?.storeSearch?.stores
            ? result?.data?.storeSearch?.stores
            : [];
        } else {
          // storeBySlug often returns "0" for distance; enrich from the user's
          // Find-a-Bakery search origin so Details matches the List page.
          store = await this.enrichBrandRefreshStoreDistance(
            store,
            addressComponents
          );
        }
      }

      this.setState({
        store,
        stores,
      });
    } catch (e) {
      this.locationErrorHandler(e.message)
    }    
  }

  /**
   * Prefer API distance from storeSearch near the user's search lat/lng.
   * Fall back to haversine when the store is missing from search results.
   */
  enrichBrandRefreshStoreDistance = async (store, addressComponents) => {
    if (!store || !isMissingBrandRefreshStoreDistance(store.distanceToStore)) {
      return store;
    }

    const originLat = parseFloat(addressComponents?.lat);
    const originLng = parseFloat(addressComponents?.lng);
    if (Number.isNaN(originLat) || Number.isNaN(originLng)) {
      return {
        ...store,
        distanceToStore: null,
      };
    }

    try {
      const result = await storeRepo.getStores({
        lat: originLat,
        lng: originLng,
      });
      const match = (result?.data?.storeSearch?.stores || []).find(
        candidate => String(candidate.id) === String(store.id)
      );
      if (
        match &&
        !isMissingBrandRefreshStoreDistance(match.distanceToStore)
      ) {
        return {
          ...store,
          distanceToStore: match.distanceToStore,
        };
      }
    } catch (e) {
      // Fall through to client-side distance.
    }

    const storeLat = parseFloat(store.lat);
    const storeLng = parseFloat(store.lng);
    if (Number.isNaN(storeLat) || Number.isNaN(storeLng)) {
      return {
        ...store,
        distanceToStore: null,
      };
    }

    const miles = metersToMiles(
      haversineDistance(
        { lat: originLat, lng: originLng },
        { lat: storeLat, lng: storeLng }
      )
    );

    return {
      ...store,
      distanceToStore: formatMilesAsStoreDistance(miles) || null,
    };
  }

  locationErrorHandler = (errorMsg) => {
    const hasUnauthorized = errorMsg.indexOf(httpStatusConstant.unauthorised) !== -1;
    if (hasUnauthorized) {
      this.setState({
        hasCaptchaError: hasUnauthorized,
        showCaptchaChallenge: hasUnauthorized,
      });
    } else {
      this.setState({
        showCaptchaChallenge: false,
        hasCaptchaError: false,
        skipGenerateNewToken: false,
      });
    }
  }

  renderCaptchaChallenge = () => {
    const { showCaptchaChallenge } = this.state;
    const { useRecaptcha } = this.props;
    return (
      (showCaptchaChallenge && useRecaptcha) ? (
        <div className={styles.errorContainer}>
          <ReCAPTCHAForm
            ref={this.recaptchaComponentRef}
            setToken={this.onVerifyCaptcha}
          />
        </div>
      ) : null
    )
  };

  onVerifyCaptcha = async (token) => {
    if (token) {
      this.setState({
        showCaptchaChallenge: false,
        skipGenerateNewToken: true,
        hasCaptchaError: false,
      });

      const { setVerificationToken, setVerificationProvider } = this.props;
      setVerificationToken(token);
      setVerificationProvider(elementConstants.challengeRecaptcha);
      
      await this.getStores();
    }
  }

  onViewMenuClick = () => {
    this.setState({
      showModal: true
    });
  };

  onCloseWidget = () => this.setState({ showModal: false });

  handleSeeMoreStores = () => {
    const { setSelectedMapStore, setWidgetValue, history } = this.props;
    setSelectedMapStore(null);
    setWidgetValue("");
    history.push(routeCountryPath(endpoints.locations));
  };

  updateOrderLocations = () => {
    const { store } = this.state;
    const { history, addressComponents, userId, setUserCartId } = this.props;
    return useMutation(UPDATE_ORDER_LOCATION, {
      onCompleted: async res => {
        const { storeId } = res.updateOrder;
        const state = reduxStore.store.getState();
        const { userCartId } = state.user;
        if (!userCartId) {
          const storeAddress = store ? {
            address1: store.mailingAddress,
            city: store.city,
            lat: store.lat,
            lng: store.lng,
            state: store.state,
            countryId: parseInt(store.countryId, 10),
            postcode: store.zip
          } : null;
          const address = addressComponents
            ? {
                address1: addressComponents.address1,
                city: addressComponents.city,
                lat: addressComponents.lat,
                lng: addressComponents.lng,
                postcode: addressComponents.postcode,
                state: addressComponents.state,
                countryId: parseInt(addressComponents.countryId, 10),
              }
            : storeAddress;

          const data = {
            storeId,
            orderTypeId: orderTypeIds.pickup,
            shippingMethod: shippingMethods.pickup,
            address,
            customerId: userId || 0
          };
          const response = await orderRepo.createCart(data);
          const { createCart } = response.data;
          setUserCartId(createCart.code);
        }
        history.push(routeCountryPath(endpoints.getMenuUrl(null)));
      }
    });
  };

  storeSelect = async (e, updateOrderLocation) => {
    const { orderProducts, userOrderMethod } = this.props;
    const { store } = this.state;
    const currentOrderNotPickup = userOrderMethod
      ? userOrderMethod !== orderMethods.pickup
      : false;
    const isItemInCart = Array.isArray(orderProducts) && orderProducts.length > 0;
    const changeStock = currentOrderNotPickup || isItemInCart;
    await handlePickupSubmit(e, changeStock, this, store, updateOrderLocation);
  };

  redirectToPdp = location => {
    const { redeemables, setRedeemableItem } = this.props;

    if (redeemables?.redeemableType) {
      setRedeemableItem({
        redeemableType: null,
        redeemableId: null
      });
      return routeCountryPath(endpoints.loyalty);
    }

    return location;
  };

  storeContainer = () => {
    const {
      store,
      showDefaultMap,
      stores,
      showModal,
      addressType,
      hasCaptchaError,
    } = this.state;
    const { phonePrefixes, addressComponents } = this.props;

    if (hasCaptchaError) {
      return (
        <div
          className={`${styles.pageWrapper} mx-auto max-w-screen-desktop
          bg-white dark:bg-gradient-from-t dark:bg-gradient-to-b dark:from-darkBlack dark:to-dark`}
        >
          { this.renderCaptchaChallenge() }
        </div>
      );
    }
    
    if (!store) {
      return <div className={`${styles.locationContainer} text-dark dark:text-white`}><Loader /></div>;
    }

    const promotionalText = store?.promotionalText
      ? store.promotionalText.split("&amp;").join("&")
      : "";

    const renderDetailView = () => (
      <div className={`${styles.locationContainer} text-dark dark:text-white`}>
        <Helmet>
          <title>{store.metaTitle || store.name}</title>
          <meta name="description" content={store.metaDescription || store.description} />
        </Helmet>
        <div className={styles.locationHero}>
          {store.isPickupOpen && store.storeType !== storeTypes.COMINGSOON ? (
            <Button
              handleSubmit={this.onViewMenuClick}
              customContainerStyles={`${styles.customButtonContainer} md:block hidden`}
              label="View Menu"
            />
          ) : null}
          <img
            alt={`${store.name} storefront`}
            src={getStorefrontImageSrc(store.storefrontImage)}
            onError={handleStorefrontImageError}
          />
        </div>

        <div className={styles.locationDetailsContainer}>
          <h3 className="text-dark dark:text-white">{store.metaHeading || store.name}</h3>
          <div className={styles.locationDetailsHeader}>
            <div>
              <p>{store.address}</p>
            </div>
            {store.storeType === storeTypes.COMINGSOON ? (
              <p className={styles.isClosed}>
                <span className={!store.isPickupOpen ? styles.isClosed : null}>
                  {"Coming Soon "}
                </span>
                |
                {` ${store.distanceToStore}`}
              </p>
            ) : (
              <p className={!store.isPickupOpen ? styles.isClosed : null}>
                <span className={!store.isPickupOpen ? styles.isClosed : null}>
                  {store.isPickupOpen ? "Open" : "Closed"}
                </span>
                {" "}
                |
                {` ${store.distanceToStore}`}
              </p>
            )}
          </div>

          <p>
            {phoneService.applyMask(store.phone, phonePrefixes)}
            {' '}
-
            {" "}
            <Link to="/contact" className={`${styles.needContactLink}`}>
              Need help?
            </Link>
          </p>

          {store.note ? <p>{store.note}</p> : null}

          <div className={styles.locationDetailsHours}>
            {store.hours
              ? store.hours.map(hour => {
                  return (
                    <div
                      key={hour.type}
                      className={`${styles.hoursColumn} text-dark dark:text-white`}
                    >
                      <h4 className="text-dark dark:text-white">
                        {hour.type === "Retail Hours"
                          ? "Pickup Hours"
                          : hour.type}
                      </h4>
                      {hour.days.map(day => {
                        return (
                          <div
                            className={styles.hourColumnItem}
                            key={`day_ ${day.day}`}
                          >
                            <p>{day.day}</p>
                            <p>{day.hour}</p>
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              : null}
          </div>
          {store.blurb ? (
            <span className={`${styles.blurbText} text-dark dark:text-white`}>
              {store.blurb}
            </span>
          ) : null}

          {store.promotionalText ? (
            <span className={styles.promotionalText}>{promotionalText}</span>
          ) : null}

          {store.isPickupOpen && store.storeType !== storeTypes.COMINGSOON ? (
            <Button
              handleSubmit={this.onViewMenuClick}
              customContainerStyles={`${styles.customButtonContainer} md:hidden block`}
              label="View Menu"
            />
          ) : null}
        </div>
      </div>
    );

    const updateOrderLocations = this.updateOrderLocations();
    const updateOrderLocation =
      updateOrderLocations && updateOrderLocations.length > 0
        ? updateOrderLocations[0]
        : null;

    const searchValue = addressComponents
      ? `${addressComponents.address1}, ${addressComponents.city}, ${addressComponents.state}`
      : store.address;

    return (
      <div
        className={`${styles.pageWrapper} mx-auto max-w-screen-desktop
          bg-white dark:bg-gradient-from-t dark:bg-gradient-to-b dark:from-darkBlack dark:to-dark`}
      >
        <div id="deliveryPickUpWidget">
          {showModal ? (
            <DeliveryPickUpWidget
              inputValue={searchValue}
              showPickUpButton
              clickPickUpButton={e => this.storeSelect(e, updateOrderLocation)}
              onClose={this.onCloseWidget}
              onAddressType={() => {}}
              addressType={addressType}
              selectedPopupStore={store}
              analyticsPageSection="Category Images"
            />
          ) : null}
        </div>
        <div className={`${styles.locationsContainer} md:flex flex-col gap-4`}>
          <div className="flex items-center justify-between flex-row-reverse">
            <div className="cursor-pointer h-[15px]">
              <div
                role="button"
                onClick={this.handleSeeMoreStores}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    this.handleSeeMoreStores();
                  }
                }}
                tabIndex={0}
                className="cursor-pointer h-[15px]"
              >
                <img
                  className="cursor-pointer h-[15px] block dark:hidden"
                  alt=""
                  src={exitIcon}
                />
              </div>
              <div
                onClick={this.handleSeeMoreStores}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    this.handleSeeMoreStores();
                  }
                }}
                tabIndex={0}
                role="button"
                className="cursor-pointer h-[15px]"
              >
                <img
                  className="h-[15px] hidden dark:block"
                  alt=""
                  src={lightExitIcon}
                />
              </div>
            </div>

            <h1
              className="
                text-dark dark:text-white
                font-congenialBlack font-black text-4xl leading-9 tracking-tighter"
            >
              {store.name}
            </h1>
          </div>

          {renderDetailView()}
        </div>
        <div className={styles.mapContainer}>
          <img
            onClick={this.handleSeeMoreStores}
            onKeyPress={() => {}}
            alt=""
            src={mobileBackIcon}
          />

          <MapComponent
            showDefaultMap={showDefaultMap}
            mode="locations"
            stores={stores}
            searchLocation={{
              lat: addressComponents ? addressComponents?.lat : store.lat,
              lng: addressComponents ? addressComponents?.lng : store.lng
            }}
            searchAddress={addressComponents}
            center={{
              lat: addressComponents ? addressComponents?.lat : store.lat,
              lng: addressComponents ? addressComponents?.lng : store.lng
            }}
            selectedStoreId={store.id}
          />
          <div
            className={`${styles.locationsContainerMobile} bg-white dark:bg-dark`}
            style={{ height: "calc(40vh)" }}
          >
            <div className="mt-8">
              <img
                onClick={this.handleSeeMoreStores}
                onKeyPress={() => {}}
                className={`${styles.exitIcon} block dark:hidden`}
                alt=""
                src={exitIcon}
              />
              <img
                onClick={this.handleSeeMoreStores}
                onKeyPress={() => {}}
                className={`${styles.exitIcon} hidden dark:block`}
                alt=""
                src={lightExitIcon}
              />
            </div>
            {renderDetailView()}
          </div>
        </div>
      </div>
    );
  };

  brandRefreshContainer = () => {
    const {
      store,
      showDefaultMap,
      showModal,
      addressType,
      hasCaptchaError,
    } = this.state;
    const { phonePrefixes, addressComponents } = this.props;

    if (hasCaptchaError) {
      return (
        <div data-region="store-details" className={brStyles.pageWrapper}>
          {this.renderCaptchaChallenge()}
        </div>
      );
    }

    if (!store) {
      return (
        <div data-region="store-details" className={brStyles.pageWrapper}>
          <div data-part="store-loader">
            <Loader />
          </div>
        </div>
      );
    }

    const isComingSoon = store.storeType === storeTypes.COMINGSOON;
    const pickupStatus = getStorePickupHoursStatus(store.hours);
    // Ticket 18774: status text from hours; View Menu also allows API isPickupOpen
    // (pickup widget behavior) + E2E bypass outside US open hours.
    const canOrder =
      (pickupStatus.isOpen ||
        !!store.isPickupOpen ||
        isE2EStoreHoursBypassEnabled()) &&
      !isComingSoon;

    const promotionalText = store?.promotionalText
      ? store.promotionalText.split("&amp;").join("&")
      : "";

    const phoneDisplay =
      formatBrandRefreshStorePhone(store.phone) ||
      phoneService.applyMask(store.phone, phonePrefixes);
    const phoneDigits = (store.phone || "").replace(/[^\d+]/g, "");
    const mapsQuery = encodeURIComponent(store.address || store.name || "");
    const distanceDisplay = formatBrandRefreshStoreDistance(
      store.distanceToStore
    );

    const statusLabel = isComingSoon ? "Coming Soon" : pickupStatus.statusText;
    const hoursMeta = !isComingSoon && pickupStatus.hoursText
      ? pickupStatus.hoursText
      : null;

    const updateOrderLocations = this.updateOrderLocations();
    const updateOrderLocation =
      updateOrderLocations && updateOrderLocations.length > 0
        ? updateOrderLocations[0]
        : null;

    const searchValue = addressComponents
      ? `${addressComponents.address1}, ${addressComponents.city}, ${addressComponents.state}`
      : store.address;

    return (
      <div data-region="store-details" className={brStyles.pageWrapper}>
        <Helmet>
          <title>{store.metaTitle || store.name}</title>
          <meta
            name="description"
            content={store.metaDescription || store.description}
          />
        </Helmet>

        <div id="deliveryPickUpWidget">
          {showModal ? (
            <DeliveryPickUpWidget
              inputValue={searchValue}
              showPickUpButton
              clickPickUpButton={e => this.storeSelect(e, updateOrderLocation)}
              onClose={this.onCloseWidget}
              onAddressType={() => {}}
              addressType={addressType}
              selectedPopupStore={store}
              analyticsPageSection="Category Images"
            />
          ) : null}
        </div>

        <div data-part="store-grid">
          <div data-part="store-info">
            <div data-part="store-title-block">
              <h1 data-part="store-name">{store.metaHeading || store.name}</h1>
              <div data-part="store-divider" aria-hidden="true" />
            </div>

            <p data-part="store-status">
              <span
                data-part="status-badge"
                data-status={
                  // eslint-disable-next-line no-nested-ternary
                  isComingSoon
                    ? "comingsoon"
                    : pickupStatus.isOpen
                    ? "open"
                    : "closed"
                }
              >
                {statusLabel}
              </span>
              {hoursMeta ? (
                <>
                  <span data-part="status-dot" aria-hidden="true">
                    {" \u00b7 "}
                  </span>
                  <span data-part="status-hours">{hoursMeta}</span>
                </>
              ) : null}
              {distanceDisplay ? (
                <>
                  <span data-part="status-dot" aria-hidden="true">
                    {" \u00b7 "}
                  </span>
                  <span data-part="status-distance">{distanceDisplay}</span>
                </>
              ) : null}
            </p>

            <div data-part="store-contact">
              <a
                data-part="store-address"
                href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {store.address}
              </a>

              {store.phone ? (
                <a data-part="store-phone" href={`tel:${phoneDigits}`}>
                  {phoneDisplay}
                </a>
              ) : null}
            </div>

            <div data-part="store-ctas">
              {canOrder ? (
                <button
                  type="button"
                  data-part="view-menu-btn"
                  onClick={this.onViewMenuClick}
                >
                  <span data-part="btn-label">View Menu</span>
                </button>
              ) : null}
              <button
                type="button"
                data-part="see-more-btn"
                onClick={this.handleSeeMoreStores}
              >
                <span data-part="btn-label">See More Stores</span>
              </button>
            </div>

            <div data-part="store-hero">
              <img
                alt={`${store.name} storefront`}
                src={getStorefrontImageSrc(store.storefrontImage)}
                onError={handleStorefrontImageError}
              />
            </div>

            {store.note ? <p data-part="store-note">{store.note}</p> : null}

            <div data-part="store-card">
              {/* Figma Container: title + hours list are siblings with gap-20 */}
              {store.hours
                ? store.hours.map((hour, index) => (
                  <React.Fragment key={hour.type}>
                    {index > 0 ? (
                      <div data-part="hours-divider" aria-hidden="true" />
                      ) : null}
                    <h4 data-part="hours-title">
                      {hour.type === "Retail Hours"
                          ? "Pickup Hours"
                          : hour.type}
                    </h4>
                    <div data-part="hours-list">
                      {hour.days.map(day => (
                        <div data-part="hours-row" key={`day_${day.day}`}>
                          <span data-part="hours-day">{day.day}</span>
                          <span data-part="hours-time">{day.hour}</span>
                        </div>
                        ))}
                    </div>
                  </React.Fragment>
                  ))
                : null}

              {(store.blurb || store.promotionalText) && store.hours ? (
                <div data-part="hours-divider" aria-hidden="true" />
              ) : null}

              {store.blurb ? (
                <p data-part="store-blurb">{store.blurb}</p>
              ) : null}
              {store.promotionalText ? (
                <p data-part="store-promo">{promotionalText}</p>
              ) : null}
            </div>
          </div>

          <div data-part="store-map">
            <MapComponent
              isBrandRefresh
              hideGeolocateControl
              disableMarkerSelection
              showDefaultMap={showDefaultMap}
              mode="locations"
              stores={[store]}
              zoom={15}
              center={{
                lat: store.lat,
                lng: store.lng
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  render() {
    if (isBrandRefreshEnabled()) {
      return <this.brandRefreshContainer />;
    }
    return <this.storeContainer />;
  }
}

Store.propTypes = {
  setSelectedMapStore: PropTypes.func.isRequired,
  setWidgetValue: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      slug: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  phonePrefixes: PropTypes.shape({}).isRequired, // Updated to allow any object
  addressComponents: PropTypes.shape({
    address1: PropTypes.string,
    lat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Allow string or number
    lng: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Allow string or number
    city: PropTypes.string,
    state: PropTypes.string,
    postcode: PropTypes.string,
    countryId: PropTypes.string
  }),
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Added userId prop
  setUserCartId: PropTypes.func, // Added setUserCartId prop
  orderProducts: PropTypes.arrayOf(PropTypes.shape({})),
  userOrderMethod: PropTypes.string, // Added userOrderMethod prop
  redeemables: PropTypes.shape({
    redeemableType: PropTypes.string,
    redeemableId: PropTypes.string
  }), // Added redeemables prop
  setRedeemableItem: PropTypes.func, // Added setRedeemableItem prop
  useRecaptcha: PropTypes.number, // Added useRecaptcha prop
  setVerificationToken: PropTypes.func.isRequired, // Added setVerificationToken prop
  setVerificationAction: PropTypes.func.isRequired, // Added setVerificationAction prop
  setVerificationProvider: PropTypes.func.isRequired // Added setVerificationProvider prop
};

Store.defaultProps = {
  addressComponents: null,
  userId: null, // Added userId prop
  setUserCartId: null, // Added setUserCartId prop
  orderProducts: [],
  userOrderMethod: null, // Added userOrderMethod prop
  redeemables: null, // Added redeemables prop
  setRedeemableItem: null, // Added setRedeemableItem prop
  useRecaptcha: null // Added useRecaptcha prop
};

const mapStateToProps = state => {
  const { addressComponents } = state.address;
  const { widgetOption, widgetValue, useRecaptcha } = state.elements;
  const {
    userCartId,
    selectedMapStore,
    userOrderMethod,
    selectedStore,
    userId
  } = state.user;
  const { phonePrefixes } = state.country;
  const {
    selectedCountry,
    allCountries,
    isCountrySelectActive
  } = state.country;
  const { redeemables } = state.loyalty;
  const orderProducts = state.order?.products || [];
  return {
    widgetOption,
    widgetValue,
    userCartId,
    addressComponents,
    orderProducts,
    selectedMapStore,
    userOrderMethod,
    phonePrefixes,
    selectedCountry,
    allCountries,
    isCountrySelectActive,
    selectedStore,
    redeemables,
    userId,
    useRecaptcha
  };
};

export const mapDispatchToProps = dispatch => ({
  setWidgetValue: value => dispatch(elementsActions.setWidgetValue(value)),
  setWidgetOption: value => dispatch(elementsActions.setWidgetOption(value)),
  setSelectedStore: value => dispatch(userActions.setSelectedStore(value)),
  setUserOrderMethod: value => dispatch(userActions.setUserOrderMethod(value)),
  setUserAddress: value => dispatch(userActions.setUserAddress(value)),
  setModalObject: value => dispatch(elementsActions.setModalObject(value)),
  addressUpdated: value => dispatch(addressActions.addressUpdated(value)),
  setUserCartId: value => dispatch(userActions.setUserCartId(value)),
  setSelectedMapStore: value =>
    dispatch(userActions.setSelectedMapStore(value)),
  setAutocompleteSessionToken: value =>
    dispatch(elementsActions.setAutocompleteSessionToken(value)),
  setSelectedCountry: value =>
    dispatch(countryActions.setSelectedCountry(value)),
  setRedeemableItem: value => dispatch(loyaltyAction.setRedeemableItem(value)),
  setVerificationToken: value =>
    dispatch(elementsActions.setVerificationToken(value)),
  setVerificationAction: value =>
    dispatch(elementsActions.setVerificationAction(value)),
  setVerificationProvider: value =>
    dispatch(elementsActions.setVerificationProvider(value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Store);
